import { BILLING_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { useBadges } from '@Hooks/useBadges';
import { type ApiInvoice, getEnumInvoiceTypeLabel } from '@Model';
import {
    Card,
    EditFrameMode,
    EditFrameShell,
    Group,
    type IEditFrame,
    MultiPageView,
    useGuiBinder,
    useTranslate,
} from '@synqo/frontend-component-library';
import { forceReadOnly } from './forceReadOnly';
import { invoiceActionItems, invoiceMenuItems } from './invoiceHeaderItems';
import { InvoiceCollectionModal, InvoiceOutputModals, InvoiceStateModals } from './InvoiceModals';
import {
    InvoiceEmailsSection,
    InvoiceMainSection,
    InvoicePaymentTermsSection,
    InvoicePositionsSection,
    InvoiceRelatedVouchersSection,
    InvoiceRemarksSection,
    InvoiceTransactionsSection,
} from './Sections';
import { useInvoiceActions } from './useInvoiceActions';

// @ai-generated 2026-09-18T07:55:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5

interface IProps {
    frame: IEditFrame<ApiInvoice>;
}

export function InvoiceFrameBody({ frame }: IProps) {
    const i18n = useTranslate('');

    const invoice = frame.data;
    const baseApiObjectPath = `${BILLING_API}/invoices/${invoice.id}`;
    const canEdit = frame.mode !== EditFrameMode.View;

    const binder = useGuiBinder<ApiInvoice>({
        data: invoice,
        meta: frame.meta,
        canEdit,
        requestUpdateState: frame.requestUpdateState,
        forceReadOnly: (name) => forceReadOnly(invoice, name),
    });

    const actions = useInvoiceActions(frame);
    const stateBadges = useBadges(invoice);

    const resourceLabel = getEnumInvoiceTypeLabel(invoice.type);
    const positionsLabel = `${i18n('Positionen')} (${invoice.positions.length})`;
    const transactionsLabel = `${i18n('Zahlungen')} (${invoice.transactions.length})`;

    // An approved invoice keeps its delete button, but the action behind it is gone.
    const frameEvents = {
        ...frame.events,
        onDelete: actions.canDelete ? frame.events.onDelete : undefined,
    };

    return (
        <EditFrameShell
            frame={frame}
            headerProps={{
                label: getFrameLabel(resourceLabel, frame.mode),
                resourceLabel: resourceLabel,
                ...frameEvents,
                actionItems: invoiceActionItems(actions, i18n),
                menuItems: invoiceMenuItems(actions, canEdit),
            }}
            bannerProps={{
                mode: frame.mode,
                loading: frame.pendingAction !== undefined,
                ...frameEvents,
            }}
        >
            <InvoiceMainSection
                binder={binder}
                baseApiObjectPath={baseApiObjectPath}
                requestUpdateState={frame.requestUpdateState}
                stateBadges={stateBadges}
                relatedCancellation={invoice.related_cancellation}
                totalNetPrice={invoice.total_net_price}
                totalGrossPrice={invoice.total_gross_price}
            />

            <InvoiceRelatedVouchersSection
                binder={binder}
                baseApiObjectPath={baseApiObjectPath}
                requestUpdateState={frame.requestUpdateState}
                type={invoice.type}
                relatedInvoice={invoice.related_invoice}
                relatedCredit={invoice.related_credit}
            />

            <InvoicePaymentTermsSection
                binder={binder}
                baseApiObjectPath={baseApiObjectPath}
                requestUpdateState={frame.requestUpdateState}
                paymentTerms={invoice.payment_terms}
                dueAt={invoice.due_at}
            />

            <InvoiceRemarksSection binder={binder} />

            <InvoiceEmailsSection emails={invoice.emails} />

            <Card name="children">
                <MultiPageView name="positions" label={positionsLabel}>
                    <Group key="positions" name="positions" label={positionsLabel}>
                        <InvoicePositionsSection
                            baseApiObjectPath={baseApiObjectPath}
                            requestUpdateState={frame.requestUpdateState}
                            stateQueueDepth={frame.stateQueueDepth}
                            refetchData={frame.refetchData}
                            positions={invoice.positions}
                            taxationMode={invoice.taxation_mode}
                            processState={invoice.process_state}
                            invoiceType={invoice.type}
                            canEditPositions={canEdit && !forceReadOnly(invoice, 'positions')}
                        />
                    </Group>
                    <Group key="transactions" name="transactions" label={transactionsLabel}>
                        <InvoiceTransactionsSection
                            baseApiObjectPath={baseApiObjectPath}
                            requestUpdateState={frame.requestUpdateState}
                            stateQueueDepth={frame.stateQueueDepth}
                            refetchData={frame.refetchData}
                            transactions={invoice.transactions}
                            taxationMode={invoice.taxation_mode}
                            processState={invoice.process_state}
                            invoiceType={invoice.type}
                            canEditTransactions={canEdit && !forceReadOnly(invoice, 'transactions')}
                            isCashInvoice={actions.isCashInvoice}
                            collectionActionText={actions.collectionActionText}
                        />
                    </Group>
                </MultiPageView>
            </Card>

            <InvoiceStateModals actions={actions} resourceLabel={resourceLabel} />
            <InvoiceCollectionModal actions={actions} invoice={invoice} requestUpdateState={frame.requestUpdateState} />
            <InvoiceOutputModals actions={actions} invoiceId={invoice.id} />
        </EditFrameShell>
    );
}
